import { schlageAccessState } from "./schlageAccessState";

export const isPendingReportRow = (row, hotel) =>
  !row.confirmationError &&
  (["pending", "deleting"].includes(schlageAccessState(row.code, hotel)) ||
    row.status === "pending");

export const isPendingKeysReport = (report, hotel) =>
  !!report && !report.loading &&
  (!!report.deliveryPending || (report.passcodesDoors || []).some(row => isPendingReportRow(row, hotel)));

// Reconcile the original operation's rows, retaining its add/update/delete
// icons and immediate-provider results. Never infer success from a failed read.
export function reconcileKeysReport(report, data, hotel, bookingId) {
  let recoveredAsyncError = !!report.recoveredAsyncError;
  const passcodesDoors = (report.passcodesDoors || []).map(row => {
    if (!schlageAccessState(row.code, hotel)) return row;
    const current = (data.passcodes || []).find(code => code._id === row.code?._id);
    const removing = report.removalOnly || row.status === "delete" ||
      row.code?.pendingOperation === "delete" || row.code?.revocationRequested;
    const attached = current && (!current.bookings || current.bookings.some(
      booking => String(booking._id || booking) === String(bookingId),
    ));
    if (removing && !attached) {
      const removed = { ...row, door: row.door || row.code.door, code: null, status: "delete" };
      if (row.status === "error" &&
          (row.code.status === "pending" || row.code.pendingOperation) &&
          !row.confirmationError) {
        recoveredAsyncError = true;
        delete removed.message;
      }
      return removed;
    }
    if (!current) return row;
    if (!attached) {
      return { ...row, confirmationError: "keysReport.operationSuperseded" };
    }
    const expectedRevocation = removing && current.revocationRequested &&
      (current.pendingOperation === "delete" || schlageAccessState(current, hotel) === "deleteFailed") &&
      current.keyboardPwd === row.code.keyboardPwd &&
      (!row.code.keyboardPwdId || current.keyboardPwdId === row.code.keyboardPwdId);
    if ((row.code.commandId && current.commandId && row.code.commandId !== current.commandId && !expectedRevocation) ||
        (row.code.issuanceAttemptId || null) !== (current.issuanceAttemptId || null)) {
      return { ...row, confirmationError: "keysReport.operationSuperseded" };
    }
    const state = schlageAccessState(current, hotel);
    const failed = ["failed", "changeFailed", "deleteFailed"].includes(state);
    // An ambiguous provider write can initially be reported as an error while
    // its persisted command remains pending. Clear that error only after this
    // same issuance is confirmed; a still-installed removal is not success.
    const recovered = row.status === "error" && !row.confirmationError &&
      (row.code.status === "pending" || row.code.pendingOperation) &&
      !removing && state === "active";
    const merged = {
      ...row,
      code: current,
      status: failed ? "error" : row.status === "pending" || recovered
        ? (removing ? "delete" : row.code.pendingOperation === "update" ? "update" : "add")
        : row.status,
      ...(failed ? { message: current.commandError } : {}),
    };
    if (recovered) {
      recoveredAsyncError = true;
      delete merged.message;
    }
    return merged;
  });
  const delivery = report.deliveryId && String(data.keyReport?.id) === String(report.deliveryId)
    ? data.keyReport : null;
  const next = {
    ...report,
    passcodesDoors,
    pending: passcodesDoors.some(row => isPendingReportRow(row, hotel)),
    confirmationError: !!data.accessRefreshError || (!!report.deliveryId && !delivery),
    ...(delivery ? {
      deliveryPending: delivery.pending,
      notifStatus: { ...report.notifStatus, ...delivery.notifStatus },
      deliveryError: delivery.status === "error" ? delivery.error : null,
    } : {}),
    ...(recoveredAsyncError ? { recoveredAsyncError: true } : {}),
  };
  if (recoveredAsyncError && delivery?.status === "success" &&
      !delivery.pending && !next.pending && !next.confirmationError &&
      ![...passcodesDoors, ...(report.ekeysDoors || [])].some(row =>
        row.status === "error" || row.confirmationError,
      )) {
    delete next.error;
    delete next.recoveredAsyncError;
  }
  if (!isPendingKeysReport(next, hotel)) next.timedOut = false;
  return next;
}
